import { generateRandomString } from "utils/utils";
import { createLogger } from "utils/logging";
import { type DataContainer, type Feature, type FeatureStatus, submitMerge } from "@otto-ec/tracking-bct";
import { benefitTag } from "../lib";
import { sendCustomError } from "@otto-ec/global-resources/apm";

const attributeNameId = "id";
const attributeNameBenefitId = "benefit-id";
const attributeNameFeature = "feature";
const attributeNameTeam = "team";
const attributeNameVariationId = "variation-id";
const attributeNameTargetContextDreson = "target-context-dreson";
const attributeNameCurrentContextDreson = "current-context-dreson";
const shouldSendTracking = "send-tracking";
const attributeNameTrackingParentId = "tracking-parent-id";
const attributeNameSendEvents = "send-events";
const attributeNameRemoveExpired = "remove-expired";
const shouldAddFadeInAnimation = "add-fade-in-animation";
const loadOnVisibility = "load-on-visibility";

const trackingState = "tracking-state";

const log = createLogger("tag", "tag");

/*                                                                                                   */
/*                                                                              */
const MAX_CLEANUP_PERIOD_IN_MILLIS = 20 * 24 * 60 * 60 * 1000;
const MIN_RATIO_TO_FADE_IN = 0.8;

export class BenefitTagElement extends HTMLElement {
	static observedAttributes = [
		attributeNameId,
		attributeNameBenefitId,
		attributeNameFeature,
		attributeNameTeam,
		attributeNameVariationId,
		attributeNameTargetContextDreson,
		attributeNameCurrentContextDreson,
		shouldSendTracking,
		attributeNameTrackingParentId,
		attributeNameSendEvents,
		attributeNameRemoveExpired,
		shouldAddFadeInAnimation,
		loadOnVisibility,
	];
	private _isConnected = false;

	override get isConnected() {
		return this._isConnected;
	}

	override set isConnected(value: boolean) {
		this._isConnected = value;
	}

	constructor() {
		super(); /*                                     */
		this.isConnected = false;
	}

	async attributeChangedCallback(name: string, oldValue: string, newValue: string) {
		log.info("Attribute update: ", name, oldValue, newValue, this.isConnected);
		this.firstElementChild?.remove();
		if (this.isConnected) {
			await this.callBackend();
		}
	}

	async connectedCallback() {
		this.isConnected = true;
		await this.callBackend();
	}

	async callBackend() {
		if (this.hasAttribute(loadOnVisibility)) {
			registerVisibilityObserver(this, () => {
				/*                                                                                                  */
				this.removeAttribute(loadOnVisibility);
			});
			return;
		}

		const id = this.getAttribute(attributeNameId);
		const urlSearchParams = this.getSearchParams();

		log.info("Calling tag for %s with %s", id, JSON.stringify(urlSearchParams));

		let tries = 0;
		let success = false;
		const status_code = [];

		while (tries++ < 3 && !success) {
			let response: Response;
			try {
				response = await fetch(`/benefit-tag/tag?${urlSearchParams}`, {
					headers: {
						Accept: "text/html;charset=UTF-8",
						"X-Requested-With": "XMLHttpRequest",
					},
				});
			} catch (error) {
				log.warn("tag request returns an error!", error);

				if (error instanceof DOMException) {
					status_code.push(error.name);
				} else {
					status_code.push(error);
				}
				continue;
			}

			status_code.push(response.status);
			success = response.ok;

			if (success) {
				/*                          */
				response.text().then((text) => {
					this.innerHTML = text;
					initTag(this);
				});
				break;
			}
			log.warn("tag endpoint returns a non ok response!", response.status);
		}

		if (!success) {
			sendCustomError("ft9-benefit-tag-not-ok", "callBackend error", {
				urlSearchParams: urlSearchParams.toString(),
				status_code: status_code.join(":"),
				tries: tries - 1,
			});
		}
	}

	private getSearchParams() {
		const urlSearchParams = new URLSearchParams({});

		function addToSearchParam(parmName: string, value: string | null) {
			if (value != null) {
				urlSearchParams.append(parmName, value);
			}
		}

		addToSearchParam("benefitId", this.getAttribute(attributeNameBenefitId));
		addToSearchParam("feature", this.getAttribute(attributeNameFeature));
		addToSearchParam("team", this.getAttribute(attributeNameTeam));
		addToSearchParam("variationId", this.getAttribute(attributeNameVariationId));
		addToSearchParam("targetContextDreson", this.getAttribute(attributeNameTargetContextDreson));
		addToSearchParam("currentContextDreson", this.getAttribute(attributeNameCurrentContextDreson));
		return urlSearchParams;
	}
}

function initTag(initializingWrapper: BenefitTagElement) {
	updateTrackingState(initializingWrapper, "loaded");

	const tag = initializingWrapper.firstElementChild;
	if (!(tag && tag instanceof HTMLElement)) {
		return;
	}

	/*                                              */
	if (initializingWrapper.hasAttribute(attributeNameRemoveExpired) && tag.dataset.expirationDate) {
		const expirationDate = new Date(tag.dataset.expirationDate);

		const delayInMillis: number = Math.min(Math.max(expirationDate.getTime() - new Date().getTime(), 1), MAX_CLEANUP_PERIOD_IN_MILLIS);
		window.setTimeout(() => {
			tag.remove();
		}, delayInMillis);
	}

	/*              */
	const trackingParentId = initializingWrapper.getAttribute(attributeNameTrackingParentId);
	sendTracking(initializingWrapper, tag, trackingParentId);

	registerVisibilityObserver(initializingWrapper, () => {
		updateTrackingState(initializingWrapper, "visible");

		if (initializingWrapper.hasAttribute(shouldAddFadeInAnimation)) {
			initializingWrapper.classList.add("benefit-tag__visible");
		}
	});

	tag.addEventListener("click", (event) => {
		event.preventDefault();
		event.stopPropagation();

		updateTrackingState(initializingWrapper, "clicked");
	});

	benefitTag.mergeTracking.subscribe((event) => {
		if (event.elementId !== initializingWrapper.getAttribute(attributeNameId)) {
			return;
		}
		sendTracking(initializingWrapper, tag, event.parentId);
	});
}

function updateTrackingState(initializingWrapper: BenefitTagElement, newState: FeatureStatus) {
	log.info("Attribute tracking state: ", newState);
	initializingWrapper.setAttribute(trackingState, newState);
}

function sendTracking(initializingWrapper: BenefitTagElement, tag: HTMLElement, trackingParentId: string | null) {
	if (!initializingWrapper.hasAttribute(shouldSendTracking)) {
		return;
	}

	const trackingStatus = initializingWrapper.getAttribute(trackingState) ?? ("unused" as FeatureStatus);

	const trackingData = tag.dataset.trackingLabels;
	if (!trackingData) {
		return;
	}

	let feature = {
		id: generateRandomString(20),
		name: "Benefit",
		status: trackingStatus,
		labels: JSON.parse(trackingData) as DataContainer,
	} as Feature;

	if (trackingParentId) {
		feature = {
			...feature,
			parentId: trackingParentId,
		};
	}

	submitMerge({}, [feature]);
}

function registerVisibilityObserver(initializingWrapper: BenefitTagElement, callback: () => void) {
	const options: IntersectionObserverInit = {
		threshold: MIN_RATIO_TO_FADE_IN,
	};

	const observer = new IntersectionObserver((entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
		for (const entry of entries) {
			if (entry.intersectionRatio >= MIN_RATIO_TO_FADE_IN) {
				callback();
				observer.unobserve(entry.target);
			}
		}
	}, options);
	observer.observe(initializingWrapper);
}

customElements.define("ft9-benefit-tag-v1", BenefitTagElement);
